import { employmentTypeSchema, ratingSummarySchema } from "api/resources/shared";
import * as z from "zod/mini";
import { proPreferencesSchema } from "./proPreferences";
import { makeApiResource } from "./resource";

export const proBadges = ["VERIFICATION", "GOLD", "SILVER", "BRONZE", "TOP_PRO"] as const;
const propBadgesOptionsSchema = z.enum(proBadges);
export const proBadgeSchema = z.pipe(
  z.array(propBadgesOptionsSchema),
  z.transform((badges) => badges?.filter((badge) => badge !== "TOP_PRO")),
);
export const sortBadges = (badges: ReadonlyArray<ProBadgeType>) =>
  badges
    .filter((badge): badge is ProBadge[number] => badge !== "TOP_PRO")
    .toSorted((a) => (a === "VERIFICATION" ? -1 : 1));

export type ProBadge = z.infer<typeof proBadgeSchema>;
export type ProBadgeType = z.infer<typeof propBadgesOptionsSchema>;

export const proBadgesTypesOptions: Array<{ value: ProBadgeType; label: string; description: string }> = [
  {
    value: "GOLD",
    label: "Gold",
    description:
      "GOLD pros have VIP perks like early access to shifts and boosted profile visibility on our matching platform!",
  },
  {
    value: "SILVER",
    label: "Silver",
    description:
      "Silver pros are rocking it! Not only do you get to flaunt your silver status to all, but you now get early access to shifts as well!",
  },
  {
    value: "BRONZE",
    label: "Bronze",
    description:
      "Bronze pros are our rising stars! Your rank is now visible to all businesses, showcasing your excellence!",
  },
];

const proPartialProfileSchema = z.object({
  uuid: z.nullish(z.string()),
  profile_id: z.nullish(z.number()),
  bio: z.nullable(z.string()),
  user_id: z.nullable(z.number()),
  headshot_public_id: z.nullable(z.string()),
  // include "TOP_PRO" in the badges for backwards compatibility but filter it out to it's not accidentally added
  badges: z.pipe(
    z.nullish(z.array(z.enum([...proBadges, "TOP_PRO"]))),
    z.transform((badges) => badges?.filter((badge) => badge !== "TOP_PRO")),
  ),
  display_name: z.nullish(z.string()),
  pro_preferences: z.nullable(z.extend(proPreferencesSchema, { id: z.nullish(z.number()) })),
  work_history: z.array(
    makeApiResource({
      business_name: z.string(),
      position: z.nullable(z.string()),
      pro: z.nullish(z.number()),
      start_date: z.string(),
      end_date: z.nullish(z.string()),
      position_skill_object: z.nullable(
        z.object({
          id: z.number(),
          name: z.string(),
          description: z.nullish(z.string()),
        }),
      ),
      created_ts: z.nullish(z.string()),
      created_by: z.nullish(z.number()),
    }),
  ),
  market_id: z.nullish(z.number()),
  market_not_listed: z.optional(z.coerce.boolean()),
  market_not_listed_name: z.optional(z.string()),

  supported_employment_types: z.optional(z.array(employmentTypeSchema)),
});
export type ProPartialProfile = z.infer<typeof proPartialProfileSchema>;

export const proSchema = makeApiResource({
  ratings_summary: z.array(ratingSummarySchema),
  completion_percentage: z.nullish(z.number()),
  gigs_worked: z.nullish(z.number()),
  verified: z.nullish(z.boolean()),
  rating: z.nullish(z.number()),
  review_count: z.nullish(z.number()),
  skill_certification_count: z.nullish(z.number()),
  display_name: z.nullable(z.string()),
  first_name: z.string(),
  last_name: z.string(),
  gigster_profile: z.nullable(proPartialProfileSchema),
  phone_number: z.nullish(z.string()),
  in_network: z.catch(z.boolean(), false),
});
export type Pro = z.infer<typeof proSchema>;
